import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@pantry/ui';
import { supabase } from '../lib/supabase';

export const Route = createFileRoute('/auth-callback')({
  component: AuthCallbackPage,
});

/**
 * Auth callback page that handles both mobile deep links and web OAuth.
 *
 * For mobile (magic links, password reset):
 * 1. On mobile with app installed: iOS/Android intercepts and opens the app directly
 * 2. On mobile without app: This page loads and tries to redirect to app store
 *
 * For web OAuth (Google, Apple sign-in):
 * 1. Supabase handles the token exchange server-side via PKCE
 * 2. Sets the session via the JS client (no tokens in URL)
 * 3. This page detects the session and redirects to dashboard
 */
function AuthCallbackPage() {
  const [status, setStatus] = useState<'checking' | 'redirecting' | 'fallback'>('checking');

  // Check if on mobile device (computed once, no need for state)
  const isMobile = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod|android/.test(userAgent);
  }, []);

  // Compute redirect path once on mount (URL won't change during component lifecycle)
  const redirectPath = useMemo(() => {
    if (typeof window === 'undefined') return '/dashboard';
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('redirect') || '/dashboard';
  }, []);

  // Track timeout IDs for cleanup
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fragment = window.location.hash;

    // Magic links and mobile deep links include tokens in the URL fragment.
    // OAuth with PKCE does NOT include tokens here - Supabase handles that server-side.
    if (fragment && fragment.includes('access_token')) {
      setStatus('redirecting');

      // For web users, redirect to dashboard/specified path
      if (!isMobile) {
        window.location.href = redirectPath;
        return;
      }

      // For mobile, try to open the app with the tokens
      const customSchemeUrl = `trackmygroceries://auth-callback${fragment}`;
      window.location.href = customSchemeUrl;

      // If still here after a delay, show fallback (app not installed)
      timeoutRef.current = setTimeout(() => {
        setStatus('fallback');
      }, 2000);
      return;
    }

    // No tokens in URL - this is likely a web OAuth callback with PKCE
    // Supabase JS client handles the session automatically, we just need to wait for it
    const checkSession = async () => {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        // Session fetch failed, redirect to login
        window.location.href = '/login';
        return;
      }

      if (data.session) {
        // Session exists, redirect to dashboard
        setStatus('redirecting');
        window.location.href = redirectPath;
      } else {
        // No session yet - wait a bit and check again (PKCE exchange may be in progress)
        timeoutRef.current = setTimeout(async () => {
          const { data: retryData, error: retryError } = await supabase.auth.getSession();

          if (retryError || !retryData.session) {
            // No session after retry - redirect to login for both mobile and web
            window.location.href = '/login';
          } else {
            setStatus('redirecting');
            window.location.href = redirectPath;
          }
        }, 1000);
      }
    };

    checkSession();

    // Cleanup timeouts on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isMobile, redirectPath]);

  const handleOpenApp = () => {
    const fragment = window.location.hash;
    window.location.href = `trackmygroceries://auth-callback${fragment}`;
  };

  const handleGoToLogin = () => {
    window.location.href = '/login';
  };

  if (status === 'checking' || status === 'redirecting') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="text-muted-foreground">
            {status === 'checking' ? <Trans>Checking...</Trans> : <Trans>Opening app...</Trans>}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full space-y-8 text-center">
        {/* Logo */}
        <div className="space-y-2">
          <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <svg
              className="w-10 h-10 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h1 className="font-display text-2xl font-semibold text-foreground">TrackMyGroceries</h1>
        </div>

        {isMobile ? (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              <Trans>Tap the button below to open the app and complete sign in.</Trans>
            </p>
            <Button onClick={handleOpenApp} className="w-full h-12">
              <Trans>Open in App</Trans>
            </Button>
            <div className="pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground mb-3">
                <Trans>Don&apos;t have the app yet?</Trans>
              </p>
              <div className="flex gap-3 justify-center">
                <a
                  href="https://apps.apple.com/app/trackmygroceries"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border hover:bg-accent transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  <span className="text-sm font-medium">
                    <Trans>App Store</Trans>
                  </span>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.trackmygroceries.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border hover:bg-accent transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.25-.84-.76-.84-1.35zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27zm3.35-4.31c.34.27.64.71.64 1.19s-.3.92-.64 1.19l-2.17 1.27-2.53-2.53 2.53-2.53 2.17 1.41zM6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z" />
                  </svg>
                  <span className="text-sm font-medium">
                    <Trans>Play Store</Trans>
                  </span>
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              <Trans>
                This link is for the mobile app. Please open it on your phone, or sign in on the
                web.
              </Trans>
            </p>
            <Button onClick={handleGoToLogin} className="w-full h-12">
              <Trans>Sign in on Web</Trans>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
